/**
 * BIND certificates service — gestión/reemplazo de certificados mTLS.
 * Apunta a los endpoints de CertMgmtFn en la HTTP API.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export type CertStatus =
  | "vigente" | "vence_pronto" | "vencido"
  | "sin_certificado" | "error";

export interface CertificateRow {
  secret_name: string;
  account:     string;
  username?:   string | null;
  cn?:         string | null;
  subject?:    string;
  issuer?:     string;
  cuit?:       string | null;
  serial?:     string;
  not_before?: string;   // ISO
  not_after?:  string;   // ISO
  status:      CertStatus;
  error?:      string;
}

export interface CertPreview {
  cn?:         string | null;
  subject?:    string;
  issuer?:     string;
  cuit?:       string | null;
  serial?:     string;
  not_before?: string;
  not_after?:  string;
  status?:     CertStatus;
  key_matches: boolean;
}

export interface ReplaceResult {
  ok:             boolean;
  new_version_id: string;
  backup_s3_key:  string | null;
  refreshed:      string[] | null;
  summary:        { cn?: string | null; not_after?: string; serial?: string };
}

export interface RollbackResult {
  ok:                  boolean;
  restored_version_id: string;
  refreshed:           string[] | null;
  summary:             Partial<CertificateRow>;
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

async function jsonOrThrow<T>(r: Response, ctx: string): Promise<T> {
  if (!r.ok) {
    const body = await r.json().catch(() => ({} as any));
    throw new Error(body.error ?? `${ctx}: ${r.status}`);
  }
  return r.json();
}

export async function listCertificates(): Promise<CertificateRow[]> {
  const r = await authFetch(`${API_BASE}/cert-mgmt/certificates`, { headers: authHeader() });
  const data = await jsonOrThrow<{ items: CertificateRow[] }>(r, "listCertificates");
  return data.items;
}

export async function previewReplace(
  account: string, pfxB64: string, password: string,
): Promise<CertPreview> {
  const r = await authFetch(
    `${API_BASE}/cert-mgmt/certificates/${encodeURIComponent(account)}/preview`,
    {
      method:  "POST",
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body:    JSON.stringify({ pfx_b64: pfxB64, password }),
    },
  );
  return jsonOrThrow<CertPreview>(r, "previewReplace");
}

export async function replaceCertificate(
  account: string, pfxB64: string, password: string,
): Promise<ReplaceResult> {
  const r = await authFetch(
    `${API_BASE}/cert-mgmt/certificates/${encodeURIComponent(account)}/replace`,
    {
      method:  "POST",
      headers: { ...authHeader(), "Content-Type": "application/json" },
      body:    JSON.stringify({ pfx_b64: pfxB64, password }),
    },
  );
  return jsonOrThrow<ReplaceResult>(r, "replaceCertificate");
}

export async function rollbackCertificate(account: string): Promise<RollbackResult> {
  const r = await authFetch(
    `${API_BASE}/cert-mgmt/certificates/${encodeURIComponent(account)}/rollback`,
    { method: "POST", headers: authHeader() },
  );
  return jsonOrThrow<RollbackResult>(r, "rollbackCertificate");
}

/** Lee un File a base64 puro (sin el prefijo data:...;base64,). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const comma = res.indexOf(",");
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
